/**
 * OMG https://mantine.dev/combobox/?e=BasicSelect
 */
import { CSSProperties, ReactNode, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Combobox, Pill, PillsInput, useCombobox } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import type { Primitive } from 'zod';

import { Checkbox } from '../checkbox/Checkbox';
import { ClearButton } from '../clearbutton/ClearButton';
import { FormLabel } from '../formlabel/FormLabel';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';

import { sortSelectedDescending } from './helpers/sortSelectedDescending';
import { FormError, SelectIcon } from './Form';
import { SelectOption } from './SelectOption';

import styles from '../input/input.module.scss';
import dropdownStyles from './dropdown.module.scss';
import pillGroupStyles from './pill-group.module.scss';

export type MultiSelectOption<Data extends Primitive | unknown = unknown> = {
  value: Data;
  label?: string;
};

export type MultiSelectProps<Data extends Primitive> = {
  className?: string;
  /** Controlled state onChange handler */
  onChange?: (value: Data[]) => void;
  /** Called when changing selection is done (on blur) */
  onCommit?: (value: Data[]) => void;
  /** Selection for controlled state */
  value?: Data[];
  /** Initial selection for uncontrolled state */
  defaultValue?: Data[];
  /** max height of dropdown scrollcontainer */
  dropdownHeight?: number | string;
  /** maximum number of tags to show inline [1,n] */
  maxVisibleTags?: number;
  /** add clear action if selection is not empty. Default: true */
  showClearAction?: boolean;
  /** disable clear action. Default: false */
  disableClearAction?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  error?: string;
  title?: string;
  inlineError?: boolean;
  data: MultiSelectOption<Data>[];
  label?: ReactNode | ReactNode[];
  loading?: boolean;
  placeholder?: string;
  style?: CSSProperties;
  withinPortal?: boolean;
  sortCallback?: (
    a: MultiSelectOption<Data>,
    b: MultiSelectOption<Data>
  ) => number;
};

function arrayHasChanged(a: unknown[], b: unknown[]) {
  return a.length !== b.length || a.some((v) => !b.includes(v));
}

export function MultiSelect<Data extends Primitive>({
  className,
  data = [],
  value: selectionControlled,
  defaultValue = [],
  placeholder,
  error,
  inlineError,
  onChange,
  label,
  loading,
  disabled,
  readOnly = false,
  onCommit,
  style,
  title,
  withinPortal = true,
  showClearAction = true,
  disableClearAction = false,
  maxVisibleTags = 2,
  dropdownHeight = '35vh',
  sortCallback,
}: MultiSelectProps<Data>) {
  maxVisibleTags = Math.max(maxVisibleTags, 1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [hasFocus, setHasFocus] = useState(false);
  const [sortedOptions, setSortedOptions] = useState<MultiSelectOption<Data>[]>(
    []
  );
  const { t } = useTranslation();
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption();
      setHasFocus(false);
      commitValue(selection);
    },
    onDropdownOpen: () => {
      setCurrentSelection(selection.map(String));
      const sorted = [...data];
      if (sortCallback) {
        sorted.sort(sortCallback);
      } else {
        sorted.sort((a, b) => sortSelectedDescending(a, b, selection));
      }
      setSortedOptions(sorted);
      setHasFocus(true);
      combobox.updateSelectedOptionIndex('active');
    },
  });
  const id = useId();
  const [search, setSearch] = useState('');
  const [selection, setSelection] = useUncontrolled({
    value: selectionControlled,
    defaultValue,
    onChange,
  });

  const [currentSelection, setCurrentSelection] = useState<null | string[]>();
  function commitValue(value: Data[]) {
    if (currentSelection == null || arrayHasChanged(currentSelection, value)) {
      setCurrentSelection(null);
      onCommit?.(value);
    }
    setSearch('');
  }

  const handleValueSelect = (val: string) => {
    const selected = data.find(
      (d) => d.value === val || String(d.value) === val
    );

    const nextSelection = selection.some((s) => s === val || String(s) === val)
      ? selection.filter((v) => String(v) !== val)
      : // eslint-disable-next-line unicorn/prefer-spread
        [...selection].concat(selected != null ? [selected.value] : []);

    setSelection(nextSelection);
  };
  const handleValueRemove = (val?: Data) => {
    if (val == null || !selection.includes(val)) {
      return;
    }
    const nextSelection = selection.filter((v) => v !== val);
    setSelection(nextSelection);
  };

  const visiblePills = selection
    .slice(selection.length - maxVisibleTags)
    .filter((e) => e != null)
    .reverse()
    .map((item, index, list) => {
      const isSelectCounter =
        index === list.length - 1 && selection.length > maxVisibleTags;
      if (isSelectCounter) {
        return (
          <Pill
            unstyled
            key={String(item)}
            withRemoveButton={false}
            onRemove={() => handleValueRemove(item)}
            className={clsx(
              pillGroupStyles['pill'],
              pillGroupStyles['pill--overflow'],
              pillGroupStyles['pill--flex-no-shrink'],
              (disabled || loading) && pillGroupStyles['pill--disabled']
            )}
          >
            {maxVisibleTags > 1 ? '+' : ''}{' '}
            {selection.length - maxVisibleTags + 1} selected
          </Pill>
        );
      }

      const pillContent =
        data.find((option) => option.value === item)?.label ?? String(item);
      return (
        <Pill
          unstyled
          key={String(item)}
          withRemoveButton={false}
          onRemove={() => handleValueRemove(item)}
          className={clsx(
            pillGroupStyles['pill'],
            String(pillContent).length > 3
              ? pillGroupStyles['pill--overflow-hidden']
              : pillGroupStyles['pill--flex-no-shrink'],
            (disabled || loading) && pillGroupStyles['pill--disabled']
          )}
          data-testid="selected-pill"
        >
          {pillContent}
        </Pill>
      );
    });

  // filter selection
  const visibleSelection = sortedOptions.filter((item) =>
    (item.label ?? String(item.value))
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );

  const selectOptions = visibleSelection.map((item) => (
    <SelectOption
      key={String(item.value)}
      value={String(item.value)}
      label={item.label ?? String(item.value)}
      selected={selection.includes(item.value)}
      checkbox
    />
  ));

  let rightSection: ReactNode | undefined;
  let largeRightSection = false;

  if (loading) {
    rightSection = <LoadingIndicator data-testid="loading-indicator" />;
  } else {
    const isClearActionActive =
      search === '' &&
      !combobox.dropdownOpened &&
      !(loading || disabled || readOnly) &&
      selection.length > 0;
    largeRightSection = showClearAction;
    rightSection = (
      <>
        {showClearAction && (
          <ClearButton
            hidden={!isClearActionActive}
            disabled={disableClearAction || disabled}
            loading={loading}
            onClear={() => {
              if (selection.length > 0) {
                setSelection([]);
                commitValue([]);
              }
            }}
          />
        )}
        <SelectIcon
          disabled={loading || disabled || readOnly}
          error={!!error}
        />
      </>
    );
  }

  return (
    <div
      title={title}
      ref={dropdownRef}
      className={className}
      style={style}
      data-testid="multi-select"
    >
      <Combobox
        store={combobox}
        onOptionSubmit={handleValueSelect}
        disabled={disabled || loading || readOnly}
        portalProps={{
          target:
            !withinPortal && dropdownRef.current
              ? dropdownRef.current
              : undefined,
        }}
        classNames={{
          footer: dropdownStyles['dropdown__footer'],
          dropdown: dropdownStyles['dropdown'],
        }}
      >
        <FormLabel
          id={id}
          error={error}
          loading={loading}
          inline={inlineError}
          label={label}
          disabled={disabled === true}
        />
        <Combobox.DropdownTarget>
          {/* wrapper around label and input-like element, wrapping html-input and pills */}
          <PillsInput
            classNames={{
              wrapper: clsx(
                styles['wrapper'],
                largeRightSection && styles['wrapper--large-right-section']
              ),
              error: styles['error'],
              input: clsx(
                styles['input'],
                hasFocus && styles['input--is-focussed'],
                loading && styles['input--is-loading'],
                error && styles['input--error']
              ),
              section: styles['section'],
            }}
            disabled={loading || disabled || readOnly}
            error={error && <FormError error={error} inline={inlineError} />}
            id={id}
            onClick={() => combobox.openDropdown()}
            rightSection={rightSection}
            rightSectionPointerEvents="none"
            unstyled
          >
            <Pill.Group unstyled className={pillGroupStyles['pills-group']}>
              {search === '' && visiblePills}
              <Combobox.EventsTarget>
                {/* actual html-input element inlined after pills */}
                <PillsInput.Field
                  className={pillGroupStyles['pill--input']}
                  data-testid="search"
                  onFocus={() => combobox.openDropdown()}
                  onBlur={() => {
                    combobox.closeDropdown();
                    setSearch('');
                  }}
                  value={search}
                  placeholder={selection.length === 0 ? placeholder : undefined}
                  onChange={(event) => {
                    combobox.updateSelectedOptionIndex();
                    setSearch(event.currentTarget.value);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Backspace' && search.length === 0) {
                      event.preventDefault();
                      handleValueRemove(selection.at(-1));
                    }
                  }}
                />
              </Combobox.EventsTarget>
            </Pill.Group>
          </PillsInput>
        </Combobox.DropdownTarget>
        <Combobox.Dropdown>
          <Combobox.Options
            mah={dropdownHeight}
            style={{ overflowY: 'auto', width: style?.width }}
          >
            {selectOptions.length > 0 ? (
              selectOptions
            ) : (
              <Combobox.Empty>
                {Array.isArray(data) && data.length > 0
                  ? t('app.select.noOptionsFound', { query: search })
                  : t('app.select.noOptionsAvailable')}
              </Combobox.Empty>
            )}
          </Combobox.Options>
          {selectOptions.length > 1 && (
            <Combobox.Footer
              data-testid="select-all"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                const modeRemove = !visibleSelection.some(
                  (item) => !selection.includes(item.value)
                );
                if (modeRemove) {
                  const toRemove = new Set(
                    visibleSelection.map((item) => item.value)
                  );
                  const nextSelection = selection.filter(
                    (value) => !toRemove.has(value)
                  );
                  setSelection(nextSelection);
                  return;
                }

                const nextSelection = [...selection];
                for (const item of visibleSelection) {
                  if (!nextSelection.includes(item.value)) {
                    nextSelection.push(item.value);
                  }
                }
                setSelection(nextSelection);
              }}
            >
              <Checkbox
                checked={
                  !visibleSelection.some(
                    (item) => !selection.includes(item.value)
                  )
                }
                label={t('app.select.selectAbove')}
                style={{ pointerEvents: 'none' }}
              />
            </Combobox.Footer>
          )}
        </Combobox.Dropdown>
      </Combobox>
    </div>
  );
}
